// ** React Imports
import { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { toast } from "helpers/toastWrapper";

// ** Custom Components
import LoadingContainer from "components/layout/LoadingContainer";
import Text from "components/typography/Text";
import TableView from "components/layout/TableView";
import Pagination from "components/layout/Pagination";
import LoginAvatarSmall from "components/userpanel/LoginAvatarSmall";
import Link from "components/layout/Link";
import VipStatus from "components/user/VipStatus";
import DatePresenter from "components/other/DatePresenter";
import Skeleton from "components/layout/Skeleton";
import Block from "components/layout/Block";
import VisitorsCount from "../../components/VisitorsCount";
import UserAvatar from "components/user/UserAvatar";
import StyledAvatarNotVip from "../../components/AvatarNotVip";

// ** Utils & Helper
import formatMessage from "utils/formatMessage";
import api from "helpers/ApiClient";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const Viewers = ({
	route: { path },
	match: {
		params: { page },
	},
}) => {
	const { t } = useTranslation(["common", "userpanel"]);
	const location = useLocation();
	const history = useHistory();

	const [viewers, setViewers] = useState({
		data: [],
		pagination: {},
		total: 0,
		loading: true,
	});

	const currentPage = Number(page || 1);

	const { isPhone } = useContext(deviceTypeContext);

	const columns = [
		{
			key: "viever",
			isMain: true,
			headerName: t("common:person"),
			rowComponent: ({ data }) => {
				if (data.id === null) {
					return (
						<Block flex alignCenter>
							<Link to="/userpanel/payments/buy">
								<StyledAvatarNotVip>
									<UserAvatar
										src={data.avatar}
										online={data.online}
										status={data.profile_status}
										privateData={data.private_data}
									/>
								</StyledAvatarNotVip>
							</Link>
							<Block ml={3}>
								<Text key={data.last_view_date}>
									{formatMessage(
										t("userpanel:visitors.seeVisitor"),
										{
											vip: (
												<Link
													to="/userpanel/payments/buy"
													color="primary"
													underline
												>
													{t(
														"userpanel:visitors.vipAccount"
													)}
													<Text
														style={{
															marginRight: "5px",
															marginLeft:
																"0.5rem",
														}}
													>
														<VipStatus
															noPad
															status={4}
															height={14}
														/>
													</Text>
													<VipStatus
														noPad
														status={5}
														height={14}
													/>
												</Link>
											),
										}
									)}
								</Text>
								{isPhone && (
									<>
										<Text color="grey" small fullLine>
											<DatePresenter
												date={data.last_view_date}
												key={data.id}
											/>
										</Text>
										<Text fullLine>
											{t(
												"userpanel:visitors.tableHeaders.howManyTimes"
											)}
											:{" "}
											<Text bold>{data.count_view}</Text>
										</Text>
									</>
								)}
							</Block>
						</Block>
					);
				} else {
					return (
						<LoginAvatarSmall
							direction={false}
							data={data}
							maxWidthWithEllipsis={isPhone ? 200 : 110}
							linkOnButtonToPayments
							additional={() => {
								return (
									isPhone && (
										<>
											<Text color="grey" small fullLine>
												<DatePresenter
													date={data.last_view_date}
													key={data.id}
												/>
											</Text>
											<Text>
												{t(
													"userpanel:visitors.tableHeaders.howManyTimes"
												)}
												:{" "}
												<Text bold>
													{data.count_view}
												</Text>
											</Text>
										</>
									)
								);
							}}
						/>
					);
				}
			},
		},
		{
			key: "count",
			showInMain: isPhone,
			headerName: t("userpanel:visitors.tableHeaders.howManyTimes"),
			dataKey: "count_view",
		},
		{
			key: "date",
			showInMain: isPhone,
			headerName: t("userpanel:visitors.tableHeaders.dateOfLastVist"),
			rowComponent: ({ data }) => (
				<DatePresenter date={data.last_view_date} key={data.id} />
			),
		},
	];

	const getViewers = (currentPage) => {
		setViewers((prev) => ({ ...prev, loading: true }));
		api.get(`/viewer/viewer`, {
			params: {
				date: "any",
				page: currentPage,
			},
		})
			.then((res) => {
				setViewers({
					data: res.data?.viewers || [],
					pagination: res.data?.pagination || {},
					total: res.data?.total || 0,
					loading: false,
				});
			})
			.catch((error) => {
				setViewers((prev) => ({ ...prev, loading: false }));
				if (error?.response?.data?.error) {
					toast.error(`${error.response.data.error}`);
				}
			});
	};

	useEffect(() => {
		if (!location.state?.forceReload) {
			getViewers(currentPage);
		}
	}, [location.pathname, currentPage]);

	useEffect(() => {
		if (location.state?.forceReload) {
			getViewers(1);
			history.replace({ ...location, state: null });
		}
	}, [location.key]);

	// #####################################################

	return (
		<Skeleton
			top={
				<Block mb={2}>
					<Text>{t("userpanel:visitors.visitorsCount")}:</Text>
					<VisitorsCount counter={viewers.total} withZero bold />
				</Block>
			}
			main={
				<LoadingContainer
					loading={viewers.loading}
					ready={!viewers.loading}
					noContentText={t("userpanel:visitors.viewedsEmpty")}
					empty={!viewers.loading && viewers.data.length === 0}
					loaderDefault
				>
					<TableView data={viewers.data} columns={columns} />
				</LoadingContainer>
			}
			pagination={
				viewers.pagination?.totalpages > 1 &&
				!viewers.loading && (
					<Pagination
						total={viewers.pagination.totalpages}
						current={Number(page || 1)}
						path={path}
					/>
				)
			}
		/>
	);
};

// #####################################################

export default Viewers;
